/**
 * DC.ONE collection "Load more products" enhancement.
 *
 * Replaces visible numbered pagination with a customer-controlled button while
 * retaining Shopify pagination as a non-JavaScript and error fallback.
 * Restores loaded products and the shopper's position after returning from a
 * product page with the browser Back button.
 */

(function () {
  'use strict';

  var SELECTORS = {
    section: '[data-section-type="collection"]',
    productList: '.product-list--collection',
    pagination: '.pagination',
    nextLink: '.pagination__next',
    countData: '[data-collection-products-count]',
    controls: '[data-dc-load-more-controls]'
  };

  var STORAGE_PREFIX = 'dc-load-more-state:';
  var STORAGE_MAX_AGE = 2 * 60 * 60 * 1000;
  var states = new WeakMap();
  var reinitialiseTimer = null;
  var restorePromise = null;

  function addStyles() {
    if (document.getElementById('dc-load-more-styles')) return;

    var style = document.createElement('style');
    style.id = 'dc-load-more-styles';
    style.textContent =
      '.dc-load-more-enabled .pagination {' +
        'position:absolute!important;' +
        'width:1px!important;' +
        'height:1px!important;' +
        'padding:0!important;' +
        'margin:-1px!important;' +
        'overflow:hidden!important;' +
        'clip:rect(0,0,0,0)!important;' +
        'white-space:nowrap!important;' +
        'border:0!important;' +
      '}' +
      '.dc-load-more {' +
        'display:flex;' +
        'flex-direction:column;' +
        'align-items:center;' +
        'gap:12px;' +
        'padding:26px 20px 34px;' +
        'text-align:center;' +
      '}' +
      '.dc-load-more__count {' +
        'margin:0;' +
        'font-size:14px;' +
        'color:rgba(var(--text-color-rgb),.7);' +
      '}' +
      '.dc-load-more__button {' +
        'min-width:220px;' +
      '}' +
      '.dc-load-more__button[disabled] {' +
        'cursor:wait;' +
        'opacity:.65;' +
      '}' +
      '.dc-load-more__error {' +
        'margin:0;' +
        'font-size:14px;' +
        'color:var(--error-color);' +
      '}';

    document.head.appendChild(style);
  }

  function preservePreviewParameters(url) {
    var currentUrl = new URL(window.location.href);

    ['preview_theme_id', '_fd', 'pb'].forEach(function (parameter) {
      if (currentUrl.searchParams.has(parameter) && !url.searchParams.has(parameter)) {
        url.searchParams.set(parameter, currentUrl.searchParams.get(parameter));
      }
    });
  }

  function getStateKey() {
    var url = new URL(window.location.href);

    ['page', 'preview_theme_id', '_fd', 'pb'].forEach(function (parameter) {
      url.searchParams.delete(parameter);
    });

    url.hash = '';
    url.searchParams.sort();

    return STORAGE_PREFIX + url.pathname + '?' + url.searchParams.toString();
  }

  function normaliseState(value) {
    var state = value && typeof value === 'object' ? value : {};

    return {
      pages: Math.max(1, parseInt(state.pages, 10) || 1),
      scrollY: Math.max(0, Number(state.scrollY) || 0),
      productPath: typeof state.productPath === 'string' ? state.productPath : '',
      pendingReturn: Boolean(state.pendingReturn),
      timestamp: Number(state.timestamp) || Date.now()
    };
  }

  function readReturnState() {
    try {
      var raw = sessionStorage.getItem(getStateKey());

      if (!raw) return normaliseState();

      var state = normaliseState(JSON.parse(raw));

      if (Date.now() - state.timestamp > STORAGE_MAX_AGE) {
        sessionStorage.removeItem(getStateKey());
        return normaliseState();
      }

      return state;
    } catch (error) {
      return normaliseState();
    }
  }

  function writeReturnState(patch) {
    var next = normaliseState(Object.assign({}, readReturnState(), patch || {}, {
      timestamp: Date.now()
    }));

    try {
      sessionStorage.setItem(getStateKey(), JSON.stringify(next));
    } catch (error) {
      // Browsing still works when session storage is unavailable.
    }

    return next;
  }

  function getTotalProducts(section) {
    var countData = section.querySelector(SELECTORS.countData);

    if (!countData) return null;

    try {
      var parsed = JSON.parse(countData.textContent);
      var match = String(parsed.productsCount || '').match(/[\d\s,.]+/);

      if (match) return parseInt(match[0].replace(/\D/g, ''), 10);
    } catch (error) {
      // Use the number of rendered cards when theme count data is unavailable.
    }

    return null;
  }

  function updateCount(section, state) {
    var productList = section.querySelector(SELECTORS.productList);

    if (!productList || !state || !state.count) return;

    var loaded = productList.children.length;
    var total = getTotalProducts(section);
    var text = total
      ? 'Showing ' + loaded + ' of ' + total + ' products'
      : 'Showing ' + loaded + ' products';

    state.count.textContent = text;

    section.querySelectorAll(
      '.collection__showing-count, .collection__products-count-showing'
    ).forEach(function (element) {
      element.textContent = text;
    });
  }

  function setLoading(state, loading) {
    state.loading = loading;
    state.button.disabled = loading;
    state.button.setAttribute('aria-busy', loading ? 'true' : 'false');
    state.button.textContent = loading ? 'Loading products…' : 'Load more products';
  }

  function finish(section, state) {
    setLoading(state, false);
    state.button.hidden = true;
    state.error.hidden = true;
    updateCount(section, state);
  }

  function fail(section, state, error) {
    console.error('[DC.ONE load more products]', error);
    setLoading(state, false);
    section.classList.remove('dc-load-more-enabled');
    state.button.hidden = true;
    state.error.hidden = false;
    state.error.textContent =
      'More products could not be loaded. Please use the page links below.';
  }

  async function loadNextPage(section, options) {
    var state = states.get(section);
    var settings = options || {};

    if (!state || state.loading) return false;

    var nextLink = section.querySelector(SELECTORS.nextLink);

    if (!nextLink) {
      finish(section, state);
      return false;
    }

    setLoading(state, true);
    state.error.hidden = true;

    try {
      var nextUrl = new URL(nextLink.href, window.location.href);
      preservePreviewParameters(nextUrl);

      var response = await fetch(nextUrl.toString(), {
        credentials: 'same-origin',
        headers: {'X-Requested-With': 'XMLHttpRequest'}
      });

      if (!response.ok) {
        throw new Error('Could not load the next collection page.');
      }

      var html = await response.text();
      var parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      var nextSection = parsedDocument.querySelector(SELECTORS.section);
      var nextProductList =
        nextSection && nextSection.querySelector(SELECTORS.productList);
      var currentProductList = section.querySelector(SELECTORS.productList);

      if (
        !nextSection ||
        !nextProductList ||
        !currentProductList ||
        !nextProductList.children.length
      ) {
        throw new Error('The next collection page did not contain products.');
      }

      Array.from(nextProductList.children).forEach(function (product) {
        currentProductList.appendChild(product);
      });

      var currentPagination = section.querySelector(SELECTORS.pagination);
      var nextPagination = nextSection.querySelector(SELECTORS.pagination);

      if (currentPagination && nextPagination) {
        currentPagination.replaceWith(nextPagination);
      } else if (currentPagination) {
        currentPagination.remove();
      }

      var currentCountData = section.querySelector(SELECTORS.countData);
      var nextCountData = nextSection.querySelector(SELECTORS.countData);

      if (currentCountData && nextCountData) {
        currentCountData.replaceWith(nextCountData);
      }

      state.pagesLoaded += 1;
      writeReturnState({pages: state.pagesLoaded});
      setLoading(state, false);
      updateCount(section, state);

      document.documentElement.dispatchEvent(
        new CustomEvent('dc:collection-products-loaded', {
          bubbles: true,
          detail: {
            url: nextUrl.toString(),
            productCount: currentProductList.children.length
          }
        })
      );

      if (!section.querySelector(SELECTORS.nextLink)) {
        finish(section, state);
      }

      if (!settings.restoring) {
        try {
          state.button.focus({preventScroll: true});
        } catch (error) {
          state.button.focus();
        }
      }

      return true;
    } catch (error) {
      fail(section, state, error);
      return false;
    }
  }

  function findProductCard(section, productPath) {
    if (!productPath) return null;

    var links = section.querySelectorAll(
      SELECTORS.productList + ' a[href*="/products/"]'
    );

    for (var index = 0; index < links.length; index += 1) {
      try {
        if (new URL(links[index].href, window.location.href).pathname === productPath) {
          return links[index].closest('.product-item') || links[index];
        }
      } catch (error) {
        // Ignore malformed third-party links and keep searching.
      }
    }

    return null;
  }

  function restorePosition(section, saved) {
    function applyPosition() {
      var productCard = findProductCard(section, saved.productPath);

      if (productCard && productCard.isConnected) {
        var top = productCard.getBoundingClientRect().top + window.scrollY - 120;
        window.scrollTo(0, Math.max(0, top));
      } else {
        window.scrollTo(0, saved.scrollY);
      }
    }

    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(applyPosition);
      window.setTimeout(applyPosition, 150);
      window.setTimeout(applyPosition, 400);
    });
  }

  async function restoreReturnState(section, state) {
    if (restorePromise) return restorePromise;

    var saved = readReturnState();

    if (!saved.pendingReturn) return null;

    restorePromise = (async function () {
      var targetPages = saved.pages;

      while (
        state.pagesLoaded < targetPages &&
        section.querySelector(SELECTORS.nextLink)
      ) {
        var loaded = await loadNextPage(section, {restoring: true});

        if (!loaded) break;
      }

      restorePosition(section, saved);
      writeReturnState({
        pendingReturn: false,
        pages: state.pagesLoaded,
        scrollY: saved.scrollY,
        productPath: ''
      });

      return saved;
    })();

    try {
      return await restorePromise;
    } finally {
      restorePromise = null;
    }
  }

  function createControls(pagination) {
    var controls = document.createElement('div');
    controls.className = 'dc-load-more';
    controls.setAttribute('data-dc-load-more-controls', '');

    var count = document.createElement('p');
    count.className = 'dc-load-more__count';
    count.setAttribute('aria-live', 'polite');

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'dc-load-more__button button button--primary';
    button.textContent = 'Load more products';

    var error = document.createElement('p');
    error.className = 'dc-load-more__error';
    error.hidden = true;
    error.setAttribute('role', 'alert');

    controls.append(count, button, error);
    pagination.insertAdjacentElement('afterend', controls);

    return {
      controls: controls,
      count: count,
      button: button,
      error: error
    };
  }

  function initialise(section) {
    var productList = section.querySelector(SELECTORS.productList);
    var pagination = section.querySelector(SELECTORS.pagination);
    var nextLink = section.querySelector(SELECTORS.nextLink);
    var existingState = states.get(section);

    if (
      existingState &&
      existingState.productList === productList &&
      existingState.controls.isConnected
    ) {
      updateCount(section, existingState);
      return;
    }

    if (!productList || !pagination || !nextLink) return;

    var existingControls = section.querySelector(SELECTORS.controls);

    if (existingControls) existingControls.remove();

    var elements = createControls(pagination);
    var state = {
      productList: productList,
      controls: elements.controls,
      count: elements.count,
      button: elements.button,
      error: elements.error,
      loading: false,
      pagesLoaded: 1
    };

    states.set(section, state);
    section.classList.add('dc-load-more-enabled');
    updateCount(section, state);

    state.button.addEventListener('click', function () {
      loadNextPage(section);
    });

    restoreReturnState(section, state);
  }

  function initialiseAll() {
    document.querySelectorAll(SELECTORS.section).forEach(initialise);
  }

  function scheduleReinitialise() {
    window.clearTimeout(reinitialiseTimer);
    reinitialiseTimer = window.setTimeout(initialiseAll, 100);
  }

  function markProductNavigation(event) {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    var link =
      event.target.closest &&
      event.target.closest(SELECTORS.productList + ' a[href*="/products/"]');

    if (!link || link.target === '_blank') return;

    var targetUrl;

    try {
      targetUrl = new URL(link.href, window.location.href);
    } catch (error) {
      return;
    }

    if (targetUrl.origin !== window.location.origin) return;

    var section = link.closest(SELECTORS.section);
    var state = section && states.get(section);

    writeReturnState({
      pendingReturn: true,
      pages: state ? state.pagesLoaded : 1,
      scrollY: window.scrollY,
      productPath: targetUrl.pathname
    });
  }

  function savePendingPosition() {
    var saved = readReturnState();

    if (!saved.pendingReturn) return;

    writeReturnState({
      scrollY: window.scrollY
    });
  }

  function start() {
    if (!document.querySelector(SELECTORS.section)) return;

    addStyles();
    initialiseAll();

    new MutationObserver(scheduleReinitialise).observe(
      document.getElementById('main') || document.body,
      {
        childList: true,
        subtree: true
      }
    );

    document.addEventListener('click', markProductNavigation, true);
    document.addEventListener('shopify:section:load', scheduleReinitialise);
    window.addEventListener('popstate', scheduleReinitialise);
    window.addEventListener('pagehide', savePendingPosition);

    window.addEventListener('pageshow', function (event) {
      if (!event.persisted) return;

      document.querySelectorAll(SELECTORS.section).forEach(function (section) {
        var state = states.get(section);

        if (state) restoreReturnState(section, state);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, {once: true});
  } else {
    start();
  }
})();
